import type { BmGridExportEvent } from "@/components/controls/grid/state/BmGridState";

/**
 * The minimal contract a grid must satisfy to be driven by the registry.
 *
 * Deliberately narrow (a "role" interface) so the registry is decoupled from
 * the full BmGrid component instance type: any object exposing a grid state id
 * and an export handler can register.
 */
export interface ExportableGrid {
  readonly gridState: { stateId: string };
  handleExport(event: BmGridExportEvent): void | Promise<void>;
}

/**
 * Global registry of the grids that are currently mounted.
 *
 * Grids add themselves on mount and remove themselves on unmount, so the
 * registry always reflects the live set without any external bookkeeping.
 * A Set keyed by object identity (rather than a Map keyed by stateId) keeps
 * register/unregister independent, which matters during route transitions
 * where an outgoing grid can unmount after an incoming grid with the same
 * stateId has already mounted.
 */
const grids = new Set<ExportableGrid>();

function register(grid: ExportableGrid): void {
  grids.add(grid);
}

function unregister(grid: ExportableGrid): void {
  grids.delete(grid);
}

/**
 * Dispatch an export instruction to every mounted grid whose state id matches.
 */
async function doExport(event: BmGridExportEvent): Promise<void> {
  for (const grid of grids) {
    if (grid.gridState.stateId === event.stateId) {
      await grid.handleExport(event);
    }
  }
}

export const activeGridRegistry = {
  register,
  unregister,
  doExport,
};
